import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useData } from "@/lib/data";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AudioPlayer } from "@/components/AudioPlayer";
import { TranscriptModal } from "@/components/TranscriptModal";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { ExportButton } from "@/components/ExportButton";
import { resolveRange, isInRange } from "@/lib/dateRange";
import { FileText, Search, RefreshCw, Phone, Clock, PhoneCall } from "lucide-react";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}
function fmtDuration(s) {
  if (!s && s !== 0) return "—";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
}

export default function CallsPage() {
  const { calls, callsLoading, callsFetchedOnce, refreshCalls } = useData();
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [preset, setPreset] = useState("today");
  const [customStart, setCustomStart] = useState(null);
  const [customEnd, setCustomEnd] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const callId = searchParams.get("call");
    if (callId && calls.length) {
      const found = calls.find((c) => c.vapi_call_id === callId || c.id === callId);
      if (found) {
        setSelected(found);
        const next = new URLSearchParams(searchParams);
        next.delete("call");
        setSearchParams(next, { replace: true });
      }
    }
  }, [calls, searchParams, setSearchParams]);

  const load = () => refreshCalls({ silent: false }).catch(() => toast.error("Failed to refresh"));
  // No initial fetch here — DataProvider handles it globally.

  const dateRange = useMemo(() => resolveRange(preset, customStart, customEnd), [preset, customStart, customEnd]);

  // Date-scoped list (before search); stats reflect this
  const scoped = useMemo(() => calls.filter((c) => isInRange(c.created_at || c.started_at, dateRange)), [calls, dateRange]);

  const filtered = scoped.filter((c) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      (c.customer_name || "").toLowerCase().includes(s) ||
      (c.agent_name || "").toLowerCase().includes(s) ||
      (c.customer_phone || "").toLowerCase().includes(s) ||
      (c.vapi_call_id || c.id || "").toLowerCase().includes(s)
    );
  });

  const exportRows = filtered.map((c) => ({
    call_id: c.vapi_call_id || c.id,
    date: c.created_at || c.started_at || "",
    agent: c.agent_name || "",
    customer_name: c.customer_name || "",
    customer_phone: c.customer_phone || "",
    duration_seconds: c.duration_seconds || 0,
    ended_reason: c.ended_reason || "",
    recording_url: c.recording_url || "",
    summary: c.summary || "",
  }));

  return (
    <div className="space-y-6" data-testid="calls-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Calls</p>
          <h1 className="text-3xl font-bold tracking-tight mt-1">Call History</h1>
          <p className="text-sm text-muted-foreground mt-1">All Call Swiper voice agent conversations, recordings and transcripts.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <DateRangeFilter
            preset={preset}
            onPresetChange={setPreset}
            customStart={customStart}
            customEnd={customEnd}
            onCustomChange={({ start, end }) => { setCustomStart(start); setCustomEnd(end); }}
            testId="calls-date-range"
          />
          <ExportButton rows={exportRows} filename="callswiper-calls" testId="calls-export-btn" />
          <Button variant="outline" size="sm" onClick={load} data-testid="calls-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${callsLoading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KpiCard icon={PhoneCall} label="Total calls" value={scoped.length} />
        <KpiCard icon={Clock} label="Avg duration" value={
          scoped.length ? fmtDuration(Math.round(scoped.reduce((a, c) => a + (c.duration_seconds || 0), 0) / scoped.length)) : "—"
        } />
        <KpiCard icon={Phone} label="Recorded" value={scoped.filter((c) => c.recording_url).length} />
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="p-3 border-b border-border flex items-center gap-2">
          <div className="relative flex-1 max-w-sm">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search calls…" className="pl-8 h-9" data-testid="calls-search-input" />
          </div>
          <span className="text-xs text-muted-foreground ml-auto">{filtered.length} of {scoped.length}</span>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-[10px] uppercase tracking-wider">Call ID</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Date</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Duration</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Agent</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Caller</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider">Recording</TableHead>
                <TableHead className="text-[10px] uppercase tracking-wider text-right">Transcript</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {!callsFetchedOnce && callsLoading && (
                <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-10">Loading…</TableCell></TableRow>
              )}
              {callsFetchedOnce && filtered.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-10">No calls found for the selected filters.</TableCell></TableRow>
              )}
              {filtered.map((c) => {
                const id = c.vapi_call_id || c.id;
                return (
                  <TableRow key={id} className="border-border hover:bg-muted/40" data-testid={`call-row-${id}`}>
                    <TableCell className="font-mono text-xs">{(id || "").slice(0, 12)}…</TableCell>
                    <TableCell className="text-sm">{fmtDate(c.created_at || c.started_at)}</TableCell>
                    <TableCell className="text-sm font-mono">{fmtDuration(c.duration_seconds)}</TableCell>
                    <TableCell className="text-sm">{c.agent_name || "—"}</TableCell>
                    <TableCell className="text-sm">
                      {c.customer_phone ? (
                        <div className="font-mono text-sm">{c.customer_phone}</div>
                      ) : c.customer_name ? (
                        <div className="font-medium">{c.customer_name}</div>
                      ) : (
                        <div className="inline-flex items-center gap-1.5 rounded-md border border-sky-500/25 bg-sky-500/10 text-sky-600 dark:text-sky-400 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
                          Web Call
                        </div>
                      )}
                      {c.customer_phone && c.customer_name && (
                        <div className="text-xs text-muted-foreground mt-0.5">{c.customer_name}</div>
                      )}
                    </TableCell>
                    <TableCell><AudioPlayer src={c.recording_url} callId={id} /></TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setSelected(c)}
                        disabled={!c.transcript}
                        data-testid={`view-transcript-btn-${id}`}
                      >
                        <FileText className="w-3.5 h-3.5 mr-1.5" /> View
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>

      <TranscriptModal call={selected} open={!!selected} onOpenChange={(o) => !o && setSelected(null)} />
    </div>
  );
}

function KpiCard({ icon: Icon, label, value }) {
  return (
    <Card className="p-4 flex items-start justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="text-2xl font-bold tracking-tight mt-1">{value}</div>
      </div>
      <div className="w-8 h-8 flex items-center justify-center rounded-md bg-primary/10 border border-primary/25 text-primary">
        <Icon className="w-4 h-4" />
      </div>
    </Card>
  );
}
